import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import * as React from 'react';
import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { mainColor, secondaryColor } from 'src/constants/stylesheet';
import { Language } from 'src/utils/Languages';
import { getAlt } from 'src/utils/legendAlt';
import Image from './Image';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      [mediaQueries.Mobile]: {
        height: `calc(100vh - ${spacing.MenuHeight.Mobile}px)`
      },
      [mediaQueries.Tablet]: {
        height: `calc(100vh - ${spacing.MenuHeight.Tablet}px)`
      },
      [mediaQueries.Laptop]: {
        height: `calc(100vh - ${spacing.MenuHeight.Laptop}px)`
      },
      [mediaQueries.Desktop]: {
        height: `calc(100vh - ${spacing.MenuHeight.Desktop}px)`
      },
      [mediaQueries.BigDesktop]: {
        height: `calc(100vh - ${spacing.MenuHeight.BigDesktop}px)`
      },
    },
    flexContainer: {
      display: 'flex',
      height: '100%',
      [mediaQueries.Mobile]: {
        paddingLeft: spacing.Gutter.Mobile,
        paddingRight: spacing.Gutter.Mobile,
        paddingBottom: spacing.Gutter.Mobile,
      },
      [mediaQueries.Tablet]: {
        paddingLeft: spacing.Gutter.Tablet,
        paddingRight: spacing.Gutter.Tablet,
        paddingBottom: spacing.Gutter.Tablet,
      },
      [mediaQueries.Laptop]: {
        paddingLeft: spacing.Gutter.Laptop,
        paddingRight: spacing.Gutter.Laptop,
        paddingBottom: spacing.Gutter.Laptop,
      },
      [mediaQueries.Desktop]: {
        paddingLeft: spacing.Gutter.Desktop,
        paddingRight: spacing.Gutter.Desktop,
        paddingBottom: spacing.Gutter.Desktop,
      },
      [mediaQueries.BigDesktop]: {
        paddingLeft: spacing.Gutter.BigDesktop,
        paddingRight: spacing.Gutter.BigDesktop,
        paddingBottom: spacing.Gutter.BigDesktop,
      },
    },
    menuContent: {
      height: '100%',
      display: 'flex',
      alignItems: 'flex-end',

      [mediaQueries.Mobile]: {
        width: '100%'
      },
      [mediaQueries.Tablet]: {
        width: `50%`,
        paddingRight: spacing.GutterGrid.Tablet / 2,
      },
      [mediaQueries.Laptop]: {
        width: `${1 / 3 * 100}%`,
        paddingRight: spacing.GutterGrid.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        width: `${1 / 3 * 100}%`,
        paddingRight: spacing.GutterGrid.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        width: `${2 / 4 * 100}%`,
        paddingRight: spacing.GutterGrid.BigDesktop / 2,
        marginRight: 'auto',
      },
    },
    imageContent: {
      position: 'relative',
      height: '100%',

      [mediaQueries.Mobile]: {
        display: 'none'
      },
      [mediaQueries.Tablet]: {
        width: `50%`,
        marginLeft: spacing.GutterGrid.Tablet / 2,
      },
      [mediaQueries.Laptop]: {
        width: `${2 / 3 * 100}%`,
        marginLeft: spacing.GutterGrid.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        width: `${2 / 3 * 100}%`,
        marginLeft: spacing.GutterGrid.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        width: `${2 / 4 * 100}%`,
        marginLeft: spacing.GutterGrid.BigDesktop / 2,
      },
    },
    artistiList: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'auto'
    },
    navLinkOuter: {
      marginBottom: 10,
      [mediaQueries.AfterMobile]: {
        '&:first-child': {
          marginTop: 'auto',
        },
      },
      '&:last-child': {
        marginBottom: 0,
      },
    },
    navLink: {
      ...stylesheet.SubTitle,
      lineHeight: 1,
      transition: 'color .4s ease',

      '&$hovered': {
        color: mainColor
      },

      '&$noHovered': {
        color: secondaryColor
      },
    },
    imageContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      transition: 'opacity .4s ease',

      '&$show': {
        opacity: 1
      }
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      objectPosition: 'bottom right',
      ...stylesheet.ObjectFitContain
    },
    show: {},
    noHovered: {},
    hovered: {},
  }));

export type Artists = ArtistsDto;

type Props = Artists;

const Artists: FC<Props> = ({ ArtistsPreviews, UID }) => {
  const cls = useStyles();
  const [current, setCurrent] = useState<number>();
  const sizes = "(max-width: 768px) 10vw, (min-width: 769px) and (max-width: 1024px) 50vw, (min-width: 1024px) 66vw";

  const navigation = ArtistsPreviews.map((artist, i) =>
    <li
      key={`nav${i}`}
      className={cls.navLinkOuter}
    >
      <Link
        to={`/${Language}/${UID}/${artist.UID}`}
        className={
          classNames(
            cls.navLink,
            current !== undefined && current !== i && cls.noHovered,
            current === i && cls.hovered,
          )
        }
        data-index={i}
        // tslint:disable-next-line:jsx-no-lambda
        onMouseOver={(e) => {
          setCurrent(i);
        }}
        // tslint:disable-next-line:jsx-no-lambda
        onMouseLeave={(e) => {
          setCurrent(undefined);
        }}
      >
        {artist.Title}
      </Link>
    </li>);

  const images = ArtistsPreviews.map((artist, i) =>
    <div
      key={`image-container${i}`}
      data-index={i}
      className={classNames(cls.imageContainer, current === i ? cls.show : null)}
    >
      <Image
        pictureRef={artist.PreviewImage}
        sizes={sizes}
        className={cls.image}
        alt={getAlt(artist.PreviewImage)}
      />
    </div >);

  return (
    <div className={cls.root}>
      <div className={cls.flexContainer}>
        <nav className={cls.menuContent}>
          <ul className={cls.artistiList}>
            {navigation}
          </ul>
        </nav>
        <div className={cls.imageContent}>
          {images}
        </div>
      </div>
    </div>
  );
};

export default Artists;


// WEBPACK FOOTER //
// ./src/Components/Artists.tsx